import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '~/lib/utils';

export type ToastVariant = 'default' | 'destructive';

export function Toast({
  open,
  onOpenChange,
  title,
  description,
  variant = 'default',
  durationMs = 4500
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}) {
  React.useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => onOpenChange(false), durationMs);
    return () => window.clearTimeout(t);
  }, [open, onOpenChange, durationMs]);

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-lg',
          variant === 'destructive' && 'border-destructive/60'
        )}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-medium',
                variant === 'destructive' && 'text-destructive'
              )}
            >
              {title}
            </p>
            {description ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Dismiss notification"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
